/**
 * Sección "Prepara tu primera marcación" de la pantalla `/listo` (QA-3 de la 005).
 *
 * Explica en lenguaje llano para qué se usan la cámara y la ubicación (solo al marcar) y ofrece un
 * botón que pide los dos permisos (`prepare-permissions.ts`). Reglas:
 *
 * - **Nunca bloquea**: es una card dentro de la pantalla de fin de activación; "Ir al inicio" sigue
 *   funcionando igual, y "Ahora no" la oculta.
 * - **No molesta si ya está hecho**: si la Permissions API confirma que los dos permisos están
 *   concedidos, no se pinta nada. Mientras se consulta tampoco (evita un parpadeo).
 * - **No guarda coordenadas ni imágenes** (FR-031): ver `prepare-permissions.ts`.
 *
 * El resultado se anuncia en una región `role="status"` (§10: nunca solo por color).
 */
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/button';
import { Card, CardDescription, CardTitle } from '../../components/ui/card';
import { t } from '../../i18n';

import {
  markingPermissionsAlreadyGranted,
  requestMarkingPermissions,
  type MarkingPermissionsDeps,
  type MarkingPermissionsResult,
} from './prepare-permissions';

type PrepPhase = 'checking' | 'idle' | 'requesting' | 'done' | 'hidden';

export interface PermissionsPrepProps {
  /** Inyectable en pruebas (cámara, geolocalización y Permissions API falsas). */
  deps?: MarkingPermissionsDeps;
}

function resultKey(result: MarkingPermissionsResult): string {
  if (result.camera === 'granted' && result.location === 'granted') {
    return 'attendance.prepare.resultGranted';
  }
  return 'attendance.prepare.resultPartial';
}

export function PermissionsPrep({ deps }: PermissionsPrepProps) {
  const [phase, setPhase] = useState<PrepPhase>('checking');
  const [result, setResult] = useState<MarkingPermissionsResult | null>(null);

  useEffect(() => {
    let active = true;
    void markingPermissionsAlreadyGranted(deps).then((granted) => {
      if (active) {
        setPhase(granted ? 'hidden' : 'idle');
      }
    });
    return () => {
      active = false;
    };
  }, [deps]);

  if (phase === 'checking' || phase === 'hidden') {
    return null;
  }

  const request = (): void => {
    setPhase('requesting');
    void requestMarkingPermissions(deps).then((outcome) => {
      setResult(outcome);
      setPhase('done');
    });
  };

  return (
    <Card data-testid="permissions-prep" data-phase={phase} className="text-left">
      <CardTitle>{t('attendance.prepare.title')}</CardTitle>
      <CardDescription>{t('attendance.prepare.body')}</CardDescription>
      {phase === 'done' && result !== null ? (
        <p
          role="status"
          data-testid="permissions-prep-result"
          data-camera={result.camera}
          data-location={result.location}
          className="mt-fm-3 text-fm-base text-fm-text"
        >
          {t(resultKey(result))}
        </p>
      ) : (
        <div className="mt-fm-4 flex flex-col gap-fm-2">
          <Button
            data-testid="permissions-prep-allow"
            variant="secondary"
            loading={phase === 'requesting'}
            loadingText={t('attendance.prepare.requesting')}
            onClick={request}
          >
            {t('attendance.prepare.allow')}
          </Button>
          <Button
            data-testid="permissions-prep-skip"
            variant="ghost"
            disabled={phase === 'requesting'}
            onClick={() => setPhase('hidden')}
          >
            {t('attendance.prepare.skip')}
          </Button>
        </div>
      )}
    </Card>
  );
}
